import {
  performRequestTypeGET,
  performRequestTypePOST,
  performRequestTypePUT,
  performRequestTypePATCH,
  performRequestTypeDELETE,
} from "../utils/servicesUtils";

export const getAllUsers = async () =>
  performRequestTypeGET("/api/users/system-users/", { errorMessage: "Failed to fetch users data" });

export const getAllEvaluatorUsers = async () =>
  performRequestTypeGET("/api/users/evaluators/", { errorMessage: "Failed to getSystemEvaluators" });

export const getConfigurableSystemUsers = async () =>
  performRequestTypeGET("/api/users/config/users/", { errorMessage: "Failed to fetch configurable system users" });

export const getAssignableVerificationUsers = async () =>
  performRequestTypeGET("/api/users/verificators/", { errorMessage: "Failed to fetch assignable verification users" });

// Body expects an array of ids
export const getUserDetails = async (body) =>
  performRequestTypePOST("/api/users/detail/", { body });

// Fetch filter configuration for dashboard
export const getFilterConfig = async () =>
  performRequestTypeGET("/api/users/system/filters/", { errorMessage: "Failed to fetch filter configuration" });

export const updatePasswordService = async (email, newPassword, resetToken = null) =>
  performRequestTypePUT("/api/users/update-password/", {
    body: { email, password: newPassword },
    tokenOverride: resetToken,
  });

// Reset Password Service: Reset password by sending email.
export const resetPasswordService = async (email) =>
  performRequestTypePUT(`/api/users/reset/password/?user_email=${email}`);

export const updateExistingUser = async (userId, data) =>
  performRequestTypePUT(`/api/users/update/${userId}/`, { body: data });

export const transferObligations = async (user_id, to_user) =>
  performRequestTypeGET(`/api/users/transfer/all/${user_id}/${to_user}/`, { errorMessage: "Failed to transferObligations" });

export const registerNewUser = async (payload) =>
  performRequestTypePOST("/api/users/register/", {
    body: payload,
    errorMessage: "Failed to register user",
  });

/**
 * Temporary mock response for the users Excel template.
 * Replace with the real fetch call once the backend endpoint is available.
 */
export const getUsersExcelTemplate = async () => {
  // Example implementation for later:
  // const response = await fetch('/api/users/excel-template/', { headers: getAuthHeaders() });
  // if (!response.ok) throw new Error('Failed to fetch users Excel template');
  // return response.json();

  return {
    columns: [
      { key: "id", label: "ID", type: "number" },
      { key: "email", label: "Email", type: "email" },
      { key: "department", label: "Department", type: "select", source: "departments" },
      { key: "role", label: "Role", type: "select", source: "roles" },
      { key: "active", label: "Active?", type: "boolean" },
    ],
    sources: {
      departments: ["Engineering", "Human Resources", "Finance", "Sales", "Operations"],
      roles: ["Administrator", "Manager", "Developer", "Analyst"],
    },
    rows: [
      { id: 1, email: "user1@example.com", department: "Engineering", role: "Developer", active: true },
      { id: 2, email: "user2@example.com", department: "Human Resources", role: "Manager", active: true },
      { id: 3, email: "user3@example.com", department: "Finance", role: "Analyst", active: false },
      { id: 4, email: "user4@example.com", department: "Sales", role: "Manager", active: true },
      { id: 5, email: "user5@example.com", department: "Operations", role: "Administrator", active: false },
    ],
  };
};

export const deleteUser = async (user_id) =>
  performRequestTypeDELETE(`/api/users/quick/delete/${user_id}/`);

export const getUserData = async (user_id) =>
  performRequestTypeGET(`/api/users/${user_id}/`);

export const setUserPermissions = async (user_id, data) =>
  performRequestTypePATCH(`/api/users/${user_id}/`, { body: data });

/**
 * Quick-create a new user.
 *
 * @param {Object} payload
 * @param {string} payload.first_name - required
 * @param {string} payload.last_name - required
 * @param {string} payload.email - required
 * @param {string} [payload.middle_name] - optional
 * @param {string} [payload.position] - optional
 * @param {'admin'|'user'} [payload.role] - optional, defaults to "user"
 * @param {boolean} [payload.global_access] - optional, defaults to false
 * @param {string[]} [payload.section_id] - required when global_access is false
 * @param {string[]} [payload.department_id] - required when global_access is false
 */
export const getScopeUsers = async () =>
  performRequestTypeGET("/api/users/my/scope/users/", { errorMessage: "Failed to fetch scope users" });

export const getAllowedUserModules = async () => {
  const data = await performRequestTypeGET("/api/users/modules/", { errorMessage: "Failed to fetch allowed modules" });
  return Array.isArray(data?.modules) ? data.modules : [];
};

export const quickCreateUser = async (payload) =>
  performRequestTypePOST("/api/users/quick/create/", { body: payload });

export const removeUserDepartment = async (targetUserId, payload) =>
  performRequestTypeDELETE(`/api/users/remove/department/${targetUserId}/`, { body: payload });

export const promoteUserToAdmin = async (targetUserId, { section_ids, department_ids }) =>
  performRequestTypePOST(`/api/users/promote/admin/${targetUserId}/`, { body: { section_ids, department_ids } });

export const assignUserAccess = async (targetUserId, { section_ids = [], department_ids = [] }) =>
  performRequestTypePOST(`/api/users/assign/access/${targetUserId}/`, { body: { section_ids, department_ids } });

export const getHierarchySections = async () =>
  performRequestTypeGET("/api/users/hierarchy/sections/", { errorMessage: "Failed to fetch hierarchy sections" });

export const getUserAccessTree = async (user_id) =>
  performRequestTypeGET(`/api/users/access-summary-tree/${encodeURIComponent(user_id)}/`, { errorMessage: "Failed to get access tree" });

export const exportUsers = async ({ columns, url_expires_in } = {}) => {
  const body = {};
  if (columns?.length) body.columns = columns;
  if (url_expires_in != null) body.url_expires_in = url_expires_in;
  return performRequestTypePOST("/api/users/export/users/", { body });
};

export const getUsersBySection = async (section_id) =>
  performRequestTypeGET(`/api/users/sections/${encodeURIComponent(section_id)}/access/users/`, { errorMessage: "Failed to get users by section" });

export const getUsersByDepartment = async (department_id) =>
  performRequestTypeGET(`/api/users/departments/${encodeURIComponent(department_id)}/access/users/`, { errorMessage: "Failed to get users by department" });

export const getUserDepartmentsBySection = async (user_id, section_id) =>
  performRequestTypeGET(`/api/users/${encodeURIComponent(user_id)}/sections/${encodeURIComponent(section_id)}/departments/`, { errorMessage: "Failed to get user departments by section" });